import _ from 'lodash';

import { getCurrentConference } from '../conference/functions';
import { VIDEO_TYPE } from '../media/constants';
import { getParticipantById } from '../participants';
import StateListenerRegistry from '../redux/StateListenerRegistry';

import { createVirtualScreenshareParticipant, fakeParticipantLeft } from './actions';
import { FakeParticipant } from './constants';

StateListenerRegistry.register(
    /* selector */ state => state['features/base/tracks'],
    /* listener */(tracks, store) => _updateScreenshareParticipants(store)
);

/**
 * Compares the old and new screenshare participant IDs and creates/removes the virtual screenshare participant
 * tiles accordingly.
 *
 * @param {Array<string>} oldScreenshareParticipantIds - List of old screenshare participant IDs.
 * @param {Array<string>} newScreenshareParticipantIds - Current list of screenshare participant IDs.
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _createOrRemoveVirtualParticipants(
        oldScreenshareParticipantIds,
        newScreenshareParticipantIds,
        store) {
    const { dispatch, getState } = store;
    const conference = getCurrentConference(getState());
    const removedScreenshareIds = _.difference(oldScreenshareParticipantIds, newScreenshareParticipantIds);
    const addedScreenshareIds = _.difference(newScreenshareParticipantIds, oldScreenshareParticipantIds);

    if (removedScreenshareIds.length) {
        removedScreenshareIds.forEach(participantId => {
            // console.log('Removing remote screenshare fake participant:', participantId);
            dispatch(fakeParticipantLeft(participantId, conference, {
                fakeParticipant: FakeParticipant.RemoteScreenShare
            }));
        });
    }

    if (addedScreenshareIds.length) {
        addedScreenshareIds.forEach(participantId => {
            // console.log('Creating remote screenshare fake participant:', participantId);
            dispatch(createVirtualScreenshareParticipant(participantId, false, conference));
        });
    }
}

/**
 * Handles creating and removing virtual screenshare participants as fake participants.
 * Uses the muted/unmuted state of desktop video tracks to detect when screen sharing starts/stops.
 * This creates thumbnails in the filmstrip for screen sharing participants that:
 * - Show the actual screen share video content in the thumbnail (unlike YouTube shared video)
 * - Can be clicked to display the screen share in large video
 * - Are marked with screen share indicators
 * - Use fakeParticipant: 'LocalScreenShare' or 'RemoteScreenShare'
 *
 * @param {*} store - The redux store.
 * @returns {void}
 */
function _updateScreenshareParticipants(store) {
    const { dispatch, getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);
    const tracks = state['features/base/tracks'];
    const { sortedRemoteVirtualScreenshareParticipants, localScreenShare } = state['features/base/mtApi'];

    // Get previous state - participant IDs who were screen sharing
    const previousRemoteScreenshareIds = [ ...sortedRemoteVirtualScreenshareParticipants.keys() ];
    const hadLocalScreenshare = Boolean(localScreenShare);

    // Current state - find all active (unmuted) desktop tracks
    let hasLocalScreenshare = false;
    let localScreenshareParticipantId = null;
    const currentRemoteScreenshareIds = [];

    // Process all tracks to find active desktop/screenshare tracks
    tracks.forEach(track => {
        // Check if track is a desktop video track and is NOT muted (active screen share)
        if (track.videoType === VIDEO_TYPE.DESKTOP && !track.mhTrack.isMuted()) {
            const participantId = track.participantId;
            const sourceName = track.mhTrack.getSourceName();

            // Create unique ID for the screen share fake participant
            // Use source name if available, otherwise fall back to participantId with suffix
            const screenshareId = sourceName || `${participantId}-screenshare`;

            // console.log('Active screenshare detected - participantId:', participantId, 'sourceName:', sourceName, 'screenshareId:', screenshareId, 'local:', track.local);

            if (track.local) {
                // Local screen share is active
                hasLocalScreenshare = true;
                localScreenshareParticipantId = screenshareId;

                // console.log('Local screenshare active - ID:', screenshareId);
            } else {
                // Remote screen share is active
                const participant = getParticipantById(state, participantId);

                if (participant) {
                    currentRemoteScreenshareIds.push(screenshareId);

                    // console.log('Remote screenshare active - ID:', screenshareId);
                } else {
                    // console.warn('Participant not found for remote screenshare:', participantId);
                }
            }
        }
    });

    // console.log('Screenshare state - Local:', { had: hadLocalScreenshare, has: hasLocalScreenshare, id: localScreenshareParticipantId });
    // console.log('Screenshare state - Remote:', { previous: previousRemoteScreenshareIds, current: currentRemoteScreenshareIds });

    // Handle local screenshare changes
    if (!hadLocalScreenshare && hasLocalScreenshare && localScreenshareParticipantId) {
        // Local screen sharing just started
        dispatch(createVirtualScreenshareParticipant(localScreenshareParticipantId, true, conference));
    } else if (hadLocalScreenshare && !hasLocalScreenshare && localScreenShare) {
        // Local screen sharing just stopped
        dispatch(fakeParticipantLeft(localScreenShare.id, conference, {
            fakeParticipant: FakeParticipant.LocalScreenShare
        }));
    }

    // Handle remote screenshare changes
    _createOrRemoveVirtualParticipants(previousRemoteScreenshareIds, currentRemoteScreenshareIds, store);
}
